import { useState, useMemo, useCallback } from "react";
import type { Field } from "~/types/api";
import type { TaskManagerDTO } from "~/types/api-custom";

type ValidationField = "name" | "brandId" | "workflowId" | "folderId" | "path";
type TaskFormErrors = Partial<Record<ValidationField, string>> & {
  taskFields?: Record<number, string>;
};

type PathCheck = {
  error?: string;
  isChecking: boolean;
};

export const useTaskValidation = (
  formData: Partial<TaskManagerDTO>,
  currentFolderId: number | null,
  workflowFields: Field[] = [],
  { error: pathError, isChecking }: PathCheck = { isChecking: false }
) => {
  const [errors, setErrors] = useState<TaskFormErrors>({});

  const isFormValid = useMemo(() => {
    if (!formData.name?.trim()) return false;
    if (!formData.brandId) return false;
    if (!formData.workflowId) return false;
    if (!formData.path?.trim()) return false;
    if (isChecking || pathError) return false;
    const hasInvalidRequiredField = workflowFields.some((field) => {
      if (!field.required) return false;
      const value = formData.taskFields?.find((f) => f.fieldId === field.id)?.value;
      return !value?.trim();
    });
    if (hasInvalidRequiredField) return false;
    return true;
  }, [
    formData.name,
    formData.brandId,
    formData.workflowId,
    formData.path,
    formData.taskFields,
    workflowFields,
    isChecking,
    pathError,
  ]);

  const validateOnSubmit = useCallback((): boolean => {
    const newErrors: TaskFormErrors = {};

    if (!formData.name?.trim()) newErrors.name = "Task name is required";
    if (!formData.brandId) newErrors.brandId = "Brand is required";
    if (!formData.workflowId) newErrors.workflowId = "Workflow is required";
    if (!formData.path?.trim()) newErrors.path = "Folder path is required";
    else if (pathError) newErrors.path = pathError;

    const fieldErrors: Record<number, string> = {};
    workflowFields.forEach((field) => {
      if (!field.required) return;
      const value = formData.taskFields?.find((f) => f.fieldId === field.id)?.value;
      if (!value?.trim()) {
        fieldErrors[field.id] = `${field.name} is required`;
      }
    });
    if (Object.keys(fieldErrors).length > 0) {
      newErrors.taskFields = fieldErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, currentFolderId, workflowFields, pathError]);

  const clearError = useCallback((field: ValidationField | `taskField-${number}`) => {
    setErrors((prev) => {
      if (field.startsWith("taskField-")) {
        const fieldId = Number(field.split("-")[1]);
        if (!prev.taskFields?.[fieldId]) return prev;
        const nextTaskFields = { ...prev.taskFields };
        delete nextTaskFields[fieldId];
        return {
          ...prev,
          taskFields: Object.keys(nextTaskFields).length > 0 ? nextTaskFields : undefined,
        };
      }
      const f = field as ValidationField;
      if (!prev[f]) return prev;
      const next = { ...prev };
      delete next[f];
      return next;
    });
  }, []);

  const mergedErrors = useMemo<TaskFormErrors>(
    () => (pathError && !errors.path ? { ...errors, path: pathError } : errors),
    [errors, pathError]
  );

  return {
    errors: mergedErrors,
    isFormValid,
    isCheckingPath: isChecking,
    validateOnSubmit,
    clearError,
  };
};
